import type { AdminBookingResponseDto } from '@pro-sto/api-client';
import { fetchClientHasAppBookings } from '@/features/bookings/fetchBookingsInRange';

/** Есть ли у клиента активные записи, созданные из мобильного приложения. */
export const hasClientAppBookings = (
  bookings: AdminBookingResponseDto[],
  clientUuid: string,
): boolean =>
  bookings.some((booking) => {
    if (booking.status === 'cancelled') return false;
    if (booking.created_by_source !== 'client_app') return false;
    const uuid = booking.client?.uuid ?? booking.client_uuid;
    return uuid === clientUuid;
  });

/**
 * Можно ли отправить имя в find-or-create (обновление профиля клиента).
 * Клиенты из приложения защищены; для admin-клиентов имя можно менять —
 * тогда оно уходит в БД и должно отображаться на всех записях этого клиента.
 *
 * Нельзя опираться только на срез календаря (период / статус / пагинация).
 * TODO: после появления registered_via в API клиента — проверять профиль, а не записи.
 */
export const canAdminUpdateClientName = (
  clientUuid: string | undefined,
  bookings: AdminBookingResponseDto[],
): boolean => {
  if (!clientUuid) return true;
  return !hasClientAppBookings(bookings, clientUuid);
};

/**
 * Решение «можно ли перезаписать имя»: сначала локальный список (быстрый отказ),
 * затем отдельный запрос записей клиента. Если клиент новый (нет uuid) — имя отправляем.
 * При ошибке API имя не отправляем, чтобы не затереть профиль клиента приложения.
 */
export const resolveCanAdminUpdateClientName = async (params: {
  clientUuid: string | undefined;
  serviceCenterUuid: string | undefined;
  localBookings: AdminBookingResponseDto[];
}): Promise<boolean> => {
  if (!params.clientUuid) return true;
  if (hasClientAppBookings(params.localBookings, params.clientUuid)) return false;
  if (!params.serviceCenterUuid) return false;

  try {
    const hasAppBookings = await fetchClientHasAppBookings(
      params.serviceCenterUuid,
      params.clientUuid,
    );
    return !hasAppBookings;
  } catch {
    return false;
  }
};
